import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Lock, Clock, ShieldCheck, Landmark, Banknote, Activity, Plug, Gauge, Zap, ChevronRight, Sparkles, Volume2, Loader2 } from "lucide-react";
import { Loading } from "@/pages/_shared";
import { UpgradeModal } from "@/components/membership/UpgradeModal";
import { useEntitlements, trackEvent } from "@/hooks/useEntitlements";
import { Reveal, CountUp } from "@/pages/_motion";
import api from "@/lib/api";

const PILL = {
  connected: ["CONNECTED", "border-emerald-200 bg-emerald-50 text-emerald-700", "bg-emerald-500"],
  included: ["ACTIVE", "border-emerald-200 bg-emerald-50 text-emerald-700", "bg-emerald-500"],
  not_connected: ["ACTION NEEDED", "border-amber-200 bg-amber-50 text-amber-700", "bg-amber-500"],
  not_available: ["PROVISIONING", "border-[#C7D2FE] bg-[#F1F0FB] text-[#4338CA]", "bg-[#5B4BE6]"],
  coming_soon: ["COMING SOON", "border-[#C7D2FE] bg-[#F1F0FB] text-[#4338CA]", "bg-[#5B4BE6]"],
  upgrade_required: ["TIER UNLOCK", "border-[#C7D2FE] bg-[#F1F0FB] text-[#4338CA]", "bg-[#5B4BE6]"],
};

function Pill({ state }) {
  const [label, cls, dot] = PILL[state] || PILL.not_available;
  return <span data-testid={`status-pill-${state}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[9px] font-bold tracking-widest ${cls}`}><span className={`h-1.5 w-1.5 rounded-full ${dot}`} />{label}</span>;
}

function ArchGauge({ readiness }) {
  const avail = readiness?.score?.available;
  const val = avail ? readiness.score.value : 0;
  const c = Math.PI * 84, off = c - (val / 100) * c;
  return (
    <div className="relative flex h-full flex-col items-center rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_4px_20px_rgba(15,23,42,0.05)]" data-testid="readiness-gauge-arch">
      <div className="self-start font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#5B4BE6]">Funding Readiness Diagnostic</div>
      <svg viewBox="0 0 200 120" className="mt-3 w-full max-w-[260px]" aria-label={avail ? `Readiness ${val} of 100` : "Readiness diagnostic pending"}>
        <path d="M16 108 A84 84 0 0 1 184 108" fill="none" strokeWidth="12" stroke="#E2E8F0" strokeLinecap="round" />
        <motion.path d="M16 108 A84 84 0 0 1 184 108" fill="none" strokeWidth="12" stroke="#5B4BE6" strokeLinecap="round" strokeDasharray={c}
          initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: avail ? off : c }} transition={{ type: "spring", stiffness: 42, damping: 9, mass: 1.1, delay: 0.3 }} />
        <text x="100" y="92" textAnchor="middle" className="font-mono" fill="#0F172A" fontSize={avail ? 40 : 15} fontWeight="800">{avail ? <CountUp to={val} /> : "PENDING"}</text>
        <text x="100" y="110" textAnchor="middle" fill="#94A3B8" fontSize="9" fontFamily="monospace" letterSpacing="2">{avail ? readiness.score.tier.toUpperCase() : "NOT YET AVAILABLE"}</text>
      </svg>
      <p className="mt-2 text-center text-[12px] leading-relaxed text-slate-500" data-testid="gauge-note">
        {avail ? `${readiness.model_version} · computed only from your verified inputs.` : "Complete at least half your business profile and two scored factors. MMT never estimates a number it can't stand behind."}
      </p>
      <div className="mt-4 grid w-full grid-cols-3 gap-2 text-center">
        {[["Missing", readiness?.counts?.missing ?? "—"], ["Locked", readiness?.counts?.locked ?? "—"], ["Profile", readiness ? `${readiness.completeness}%` : "—"]].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-slate-200 bg-[#F8FAFC] py-2"><div className="font-mono text-[15px] font-bold text-[#0F172A]">{v}</div><div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">{l}</div></div>
        ))}
      </div>
    </div>
  );
}

const VAULTS = [
  { key: "business_credit.dnb", id: "bureau-credit", label: "Bureau credit file", icon: Landmark, route: "/business-credit", blurb: "PAYDEX, tradelines, file depth.", why: "Lenders read your bureau file before they read your pitch.", unlocks: "Real PAYDEX + reported tradelines, refreshed from D&B." },
  { key: "bank_connection", id: "bank-sync", label: "Bank sync", icon: Banknote, route: "/cash-flow", blurb: "Verified deposits & balances.", why: "Verified deposits replace estimates with proof of revenue.", unlocks: "Deposit activity and average balances inside your readiness model." },
  { key: "cash_flow", id: "cash-flow", label: "Cash flow intelligence", icon: Activity, route: "/cash-flow", blurb: "Revenue, burn, runway.", why: "Cash flow is the strongest predictor of repayment.", unlocks: "Revenue vs. expenses, runway and signals — once a bank is linked." },
  { key: "rent_bill_reporting", id: "rent-bill", label: "Rent & bill reporting", icon: Plug, route: "/rent-bill-reporting", blurb: "Recurring payments → history.", why: "Payments you already make can become positive history.", unlocks: "Enroll, verify and queue payments for reporting." },
];

function VaultTile({ v, f, i, onUpgrade }) {
  const navigate = useNavigate();
  const st = f?.state || "not_available";
  const go = () => st === "upgrade_required" ? onUpgrade(f) : navigate(v.route);
  return (
    <Reveal delay={i * 0.06}>
      <button onClick={go} data-testid={`entitlement-tile-${v.id}`}
        className="group flex h-full w-full flex-col rounded-xl border border-slate-200 bg-white p-4 text-left transition-all duration-300 hover:-translate-y-1 hover:border-[#C7D2FE] hover:shadow-[0_12px_30px_rgba(91,75,230,0.12)]">
        <div className="flex items-start justify-between gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F1F0FB] text-[#5B4BE6] transition-colors group-hover:bg-[#5B4BE6] group-hover:text-white"><v.icon size={16} /></span>
          {st === "upgrade_required" ? <Lock size={14} className="text-[#5B4BE6]" /> : <Pill state={st} />}
        </div>
        <div className="mt-3 font-head text-[14px] font-bold text-[#0F172A]">{v.label}</div>
        <div className="text-[11px] text-slate-500">{v.blurb}</div>
        <div className="mt-3 border-t border-slate-100 pt-2.5">
          <div className="font-mono text-[8px] font-bold uppercase tracking-wider text-slate-400">Why it matters</div>
          <p className="text-[11px] leading-snug text-slate-500">{v.why}</p>
          <div className="mt-1.5 font-mono text-[8px] font-bold uppercase tracking-wider text-slate-400">{st === "upgrade_required" ? `Unlocks with ${f.required_plan}` : "What you get"}</div>
          <p className="text-[11px] leading-snug text-slate-600">{v.unlocks}</p>
        </div>
        <div className="mt-3 flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#5B4BE6]">
          {st === "upgrade_required" ? `Unlock with ${f.required_plan}` : st === "not_connected" ? "Connect now" : "Open"} <ChevronRight size={12} className="transition-transform group-hover:translate-x-0.5" />
        </div>
      </button>
    </Reveal>
  );
}

function BriefingCard() {
  const [b, setB] = useState(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => { api.get("/journey/briefing").then(({ data }) => setB(data)).catch(() => {}); }, []);
  const play = async () => {
    if (playing) return;
    setPlaying(true);
    try {
      const { data } = await api.get("/journey/briefing/audio.mp3", { responseType: "blob" });
      const audio = new Audio(URL.createObjectURL(data));
      audio.onended = () => setPlaying(false);
      audio.onerror = () => setPlaying(false);
      await audio.play();
    } catch { setPlaying(false); }
  };
  if (!b) return null;
  const rows = [["What changed", b.what_changed], ["What's blocking", b.blocking], ["The move", b.the_move]].filter(([, v]) => v);
  return (
    <Reveal>
      <section data-testid="ai-briefing-card" className="overflow-hidden rounded-2xl border border-[#C7D2FE] bg-[#F1F0FB] p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#5B4BE6]"><Sparkles size={12} /> AI Coach Briefing</div>
            <h2 className="mt-2 font-head text-xl font-bold text-[#0F172A]">{b.headline}</h2>
          </div>
          <button onClick={play} disabled={playing} data-testid="briefing-play" className="flex shrink-0 items-center gap-2 rounded-xl bg-[#5B4BE6] px-4 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-[#4F46E5] disabled:opacity-60">
            {playing ? <Loader2 size={15} className="animate-spin" /> : <Volume2 size={15} />} {playing ? "Playing…" : "Play"}
          </button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {rows.map(([l, v]) => (
            <div key={l} className="rounded-xl border border-[#C7D2FE]/70 bg-white p-4">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[#5B4BE6]">{l}</div>
              <p className="mt-1 text-[12.5px] leading-relaxed text-slate-600">{v}</p>
            </div>
          ))}
        </div>
      </section>
    </Reveal>
  );
}

export default function CustomerHome() {
  const { ent } = useEntitlements();
  const [journey, setJourney] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [modal, setModal] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    api.get("/journey/me").then(({ data }) => setJourney(data)).catch(() => setJourney(null));
    api.get("/journey/timeline").then(({ data }) => setTimeline(data.events || [])).catch(() => setTimeline([]));
    api.get("/funding-readiness/full").then(({ data }) => setReadiness(data)).catch(() => setReadiness(null));
  };
  useEffect(() => { load(); }, []);
  if (!journey) return <Loading />;

  const nba = journey.next_best_action;
  const openUpgrade = (f) => { trackEvent("locked_feature_viewed", { feature: f.key || f.feature }); setModal({ feature: f.key || f.feature, requiredPlan: f.required_plan, returnRoute: "/home" }); };
  const doNba = () => nba && (nba.status === "upgrade_required" ? openUpgrade(nba) : navigate(nba.cta));
  const plan = ent?.plan || "—";

  return (
    <div data-testid="customer-home">
      <div className="grid gap-5 lg:grid-cols-12">
        {/* HERO COMMAND MODULE */}
        <Reveal className="lg:col-span-7">
          <section className="relative h-full overflow-hidden rounded-2xl bg-[#0B1020] p-6 sm:p-8" data-testid="hero-command-module">
            <div className="absolute inset-0 bg-gradient-to-br from-[#5B4BE6]/25 via-transparent to-transparent" />
            <div className="relative">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#A5B4FC]">Command Center</span>
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">live · {plan} tier</span>
              </div>
              <h1 className="mt-3 font-head text-3xl font-extrabold leading-[1.04] tracking-tight text-white sm:text-4xl">Your capital command center.</h1>
              <p className="mt-3 max-w-md text-[14px] leading-relaxed text-slate-300">Every signal here is real: your profile, your entitlements, your connected data. Nothing estimated, nothing staged.</p>

              <div className="mt-6 grid grid-cols-3 gap-2 sm:max-w-md">
                {[["Journey", journey.completed, `/${journey.total}`], ["Unlocked", Object.values(ent?.features || {}).filter((f) => ["included", "connected", "not_connected"].includes(f.state)).length, ""], ["Locked", ent?.locked_count ?? 0, ""]].map(([l, v, suf]) => (
                  <div key={l} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2"><div className="font-mono text-[18px] font-bold text-white"><CountUp to={v} />{suf}</div><div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">{l}</div></div>
                ))}
              </div>

              {nba && (
                <div className="mt-6 flex flex-col gap-3 rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between" data-testid="next-best-action">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-[#A5B4FC]"><Zap size={11} /> Next best action</div>
                    <div className="mt-1 font-head text-[17px] font-extrabold text-white" data-testid="nba-label">{nba.status === "upgrade_required" ? `Upgrade to ${nba.required_plan}` : nba.label}</div>
                    <p className="mt-0.5 truncate text-[12px] text-slate-400">{nba.status === "upgrade_required" ? `Unlock ${nba.label.toLowerCase()} with the ${nba.required_plan} membership.` : nba.detail}</p>
                  </div>
                  <button onClick={doNba} data-testid="nba-cta" className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-[#5B4BE6] px-5 py-2.5 font-head text-[13px] font-bold text-white transition-colors hover:bg-[#4F46E5]">
                    {nba.status === "upgrade_required" ? <><Lock size={14} /> Unlock</> : <>Continue <ArrowRight size={15} /></>}
                  </button>
                </div>
              )}
            </div>
          </section>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-5"><ArchGauge readiness={readiness} /></Reveal>
      </div>

      <div className="mt-6"><BriefingCard /></div>

      {/* VAULT TILES */}
      <div className="mt-8 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-head text-lg font-bold text-[#0F172A]"><Gauge size={16} className="text-[#5B4BE6]" /> Data vaults</h2>
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">membership → connection → data</span>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {VAULTS.map((v, i) => <VaultTile key={v.id} v={v} f={ent?.features?.[v.key]} i={i} onUpgrade={openUpgrade} />)}
      </div>

      <div className="mt-8 grid gap-5 lg:grid-cols-12">
        {/* JOURNEY */}
        <Reveal className="lg:col-span-7">
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.05)]" data-testid="journey-progress-checklist">
            <div className="flex items-center justify-between">
              <h2 className="font-head text-[16px] font-bold text-[#0F172A]">Financial setup journey</h2>
              <span className="font-mono text-[11px] font-bold text-[#5B4BE6]" data-testid="journey-progress">{journey.completed}/{journey.total}</span>
            </div>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <motion.div className="h-full rounded-full bg-[#5B4BE6]" initial={{ width: 0 }} animate={{ width: `${journey.progress}%` }} transition={{ duration: 1.1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }} />
            </div>
            <div className="mt-3 divide-y divide-slate-100">
              {journey.steps.map((s, i) => (
                <button key={s.id} onClick={() => s.status === "upgrade_required" ? openUpgrade({ key: s.feature, required_plan: s.required_plan }) : (s.status !== "done" && s.status !== "locked" && navigate(s.cta))}
                  data-testid={`journey-step-${s.id}`}
                  className={`group flex w-full items-center gap-3 px-2 py-3 text-left transition-colors ${s.status === "done" || s.status === "locked" ? "" : "hover:bg-slate-50"}`}>
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border font-mono text-[10px] font-bold ${s.status === "done" ? "border-emerald-300 bg-emerald-50 text-emerald-600" : s.status === "upgrade_required" ? "border-[#C7D2FE] text-[#5B4BE6]" : s.status === "locked" ? "border-slate-200 text-slate-400" : "border-[#C7D2FE] text-[#5B4BE6]"}`}>
                    {s.status === "done" ? <CheckCircle2 size={14} /> : s.status === "upgrade_required" ? <Lock size={12} /> : i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className={`text-[13px] font-semibold ${s.status === "done" ? "text-slate-400 line-through" : "text-slate-800"}`}>{s.label}</div>
                    {s.status !== "done" && <div className="truncate text-[11px] text-slate-500">{s.detail}</div>}
                  </div>
                  {s.status === "upgrade_required" && <span className="shrink-0 rounded-full border border-[#C7D2FE] bg-[#F1F0FB] px-2 py-0.5 font-mono text-[9px] font-bold uppercase text-[#4338CA]">{s.required_plan}</span>}
                  {s.status !== "done" && s.status !== "locked" && <ArrowRight size={14} className="shrink-0 text-slate-300 group-hover:text-[#5B4BE6]" />}
                </button>
              ))}
            </div>
          </section>
        </Reveal>

        {/* TIMELINE */}
        <Reveal delay={0.1} className="lg:col-span-5">
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.05)]" data-testid="financial-timeline">
            <h2 className="font-head text-[16px] font-bold text-[#0F172A]">Financial timeline</h2>
            {timeline.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center"><Clock size={22} className="text-slate-300" /><p className="text-[12px] text-slate-500">Your activity appears here as you build.</p></div>
            ) : (
              <ol className="relative mt-4 space-y-4 pl-4">
                <span className="absolute left-[5px] top-1 h-[calc(100%-0.5rem)] w-px bg-gradient-to-b from-[#5B4BE6]/50 to-transparent" />
                {timeline.map((e, i) => (
                  <li key={i} className="relative" data-testid="timeline-event">
                    <span className="absolute -left-4 top-1 h-2.5 w-2.5 rounded-full bg-[#5B4BE6] ring-4 ring-white" />
                    <div className="text-[13px] font-semibold text-slate-800">{e.title}</div>
                    {e.detail && <div className="text-[11px] text-slate-500">{e.detail}</div>}
                    <div className="font-mono text-[10px] text-slate-400">{new Date(e.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                  </li>
                ))}
              </ol>
            )}
            <button onClick={() => navigate("/adaptive")} data-testid="open-adaptive-btn" className="mt-4 flex w-full items-center justify-between rounded-xl border border-slate-200 bg-[#F8FAFC] px-4 py-3 text-left transition-colors hover:border-[#C7D2FE]">
              <div><div className="flex items-center gap-1.5 font-head text-[13px] font-bold text-[#0F172A]"><Sparkles size={13} className="text-[#5B4BE6]" /> Open Adaptive Workspace</div><div className="text-[11px] text-slate-500">Describe a goal — MMT composes the workspace.</div></div>
              <ArrowRight size={15} className="text-slate-400" />
            </button>
          </section>
        </Reveal>
      </div>

      <p className="mt-10 flex items-center justify-center gap-2 text-center text-[11px] text-slate-500">
        <ShieldCheck size={13} /> Your command center adapts to your real data — nothing is estimated or fabricated. Funding is subject to lender eligibility and approval.
      </p>
      {modal && ent && <UpgradeModal open onClose={() => { setModal(null); load(); }} feature={modal.feature} requiredPlan={modal.requiredPlan} returnRoute={modal.returnRoute} ent={ent} />}
    </div>
  );
}
